/**
 * Village PM - Size Localization Module
 *
 * Custom tab-based size picker with automatic country detection
 * and localized size display (US/EU/UK/JPN)
 *
 * @version 2.0.0
 */

(function () {
  'use strict';

  // ============================================
  // CONFIGURATION
  // ============================================

  const CONFIG = {
    enabled: true,
    debug: false,
    storageKey: 'vpm_size_system',
    countryStorageKey: 'vpm_detected_country',
    selectedSizeKey: 'vpm_selected_size',
    defaultSystem: 'EU',
    animationDuration: 300
  };

  // ============================================
  // SIZE CONVERSION TABLE (Official Village PM)
  // ============================================

  const SIZE_CONVERSIONS = {
    '36': { US: '4', UK: '3.5', JPN: '22' },
    '37': { US: '4.5', UK: '4', JPN: '22.5' },
    '38': { US: '5.5', UK: '5', JPN: '23.5' },
    '39': { US: '6.5', UK: '5.5', JPN: '24.5' },
    '40': { US: '7', UK: '6', JPN: '25' },
    '40.5': { US: '7.5', UK: '6.5', JPN: '25.5' },
    '41': { US: '8', UK: '7', JPN: '26' },
    '42': { US: '8.5', UK: '7.5', JPN: '26.5' },
    '42.5': { US: '9', UK: '8', JPN: '27' },
    '43': { US: '9.5', UK: '8.5', JPN: '27.5' },
    '44': { US: '10', UK: '9', JPN: '28' },
    '44.5': { US: '10.5', UK: '9.5', JPN: '28.5' },
    '45': { US: '11', UK: '10', JPN: '29' },
    '46': { US: '12', UK: '11', JPN: '30' }
  };

  // ============================================
  // COUNTRY TO SIZE SYSTEM MAPPING
  // ============================================

  const COUNTRY_TO_SYSTEM = {
    // US System
    'US': 'US', 'CA': 'US', 'AU': 'US', 'NZ': 'US',
    'SG': 'US', 'HK': 'US', 'MY': 'US', 'PH': 'US',
    'ID': 'US', 'TH': 'US', 'TW': 'US', 'MO': 'US',
    'AR': 'US', 'BR': 'US', 'MX': 'US',

    // UK System
    'GB': 'UK', 'GI': 'UK', 'GG': 'UK', 'JE': 'UK', 'ZA': 'UK',

    // JPN System (displays CM values)
    'JP': 'JPN', 'KR': 'JPN', 'CN': 'JPN'

    // All others default to EU
  };

  const SYSTEM_LABELS = {
    'US': 'US M',
    'EU': 'EU',
    'UK': 'UK',
    'JPN': 'JPN'
  };

  const SYSTEM_ORDER = ['US', 'EU', 'UK', 'JPN'];

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  function log(...args) {
    if (CONFIG.debug || (window.VPMSizeConfig && window.VPMSizeConfig.debug)) {
      console.log('[VPM Size]', ...args);
    }
  }

  /**
   * Parse size string like "US7 / EU40" or plain "40" into components
   */
  function parseSizeString(sizeString) {
    if (!sizeString) return null;

    const result = {
      original: sizeString,
      US: null,
      EU: null,
      UK: null,
      JPN: null
    };

    // Extract US and EU from "US7 / EU40" format
    const usMatch = sizeString.match(/US\s*([\d.]+)/i);
    const euMatch = sizeString.match(/EU\s*([\d.]+)/i);

    if (usMatch) result.US = usMatch[1];
    if (euMatch) result.EU = euMatch[1];

    // Handle plain numbers (Shopify Markets localizes to just "36", "40", etc. for EU)
    if (!result.US && !result.EU) {
      const plainMatch = sizeString.match(/^([\d.]+)$/);
      if (plainMatch) {
        result.EU = plainMatch[1];
        log('Detected plain EU size:', plainMatch[1]);
      }
    }

    // Calculate UK and JPN from EU using conversion table
    if (result.EU && SIZE_CONVERSIONS[result.EU]) {
      const conversion = SIZE_CONVERSIONS[result.EU];
      result.UK = conversion.UK;
      result.JPN = conversion.JPN;
      if (!result.US) result.US = conversion.US;
    }

    log('Parsed size:', sizeString, '->', result);
    return result;
  }

  /**
   * Format size value for display
   */
  function formatSizeDisplay(value, system) {
    if (!value) return '';

    switch (system) {
      case 'JPN':
        return value; // Just the number (e.g., "22", "25.5")
      case 'US':
      case 'UK':
      case 'EU':
      default:
        return value; // Just the number for the list
    }
  }

  /**
   * Format size for collapsed state display
   */
  function formatSizeLabel(value, system) {
    if (!value) return '';

    switch (system) {
      case 'JPN':
        return `${value} cm`;
      case 'US':
        return `US ${value}`;
      case 'UK':
        return `UK ${value}`;
      case 'EU':
      default:
        return `EU ${value}`;
    }
  }

  // ============================================
  // COUNTRY DETECTION
  // ============================================

  /**
   * Get the current country for size system detection.
   * Priority:
   * 1. Shopify's selected country (from footer country selector) - user explicitly chose this
   * 2. IP-detected country (from browsing_context_suggestions.json) - fresh on each page load
   * 3. Default fallback
   */
  async function detectCountry() {
    // Priority 1: Use Shopify's localization country (set via footer country selector)
    // This is the most reliable as the user explicitly chose it
    if (window.VPMSizeConfig && window.VPMSizeConfig.shopifyCountry) {
      const shopifyCountry = window.VPMSizeConfig.shopifyCountry;
      log('Using Shopify localization country:', shopifyCountry);
      return shopifyCountry;
    }

    // Priority 2: Detect via IP using Shopify's geolocation API
    // No caching - fresh detection on each page load for VPN compatibility
    try {
      const response = await fetch(
        window.Shopify.routes.root + 'browsing_context_suggestions.json?country[enabled]=true'
      );

      if (!response.ok) throw new Error('Failed to fetch geolocation');

      const data = await response.json();
      const country = data.detected_values?.country?.handle;

      if (country) {
        log('IP-detected country:', country);
        return country;
      }
    } catch (error) {
      log('Country detection error:', error);
    }

    return null;
  }

  /**
   * Get the current country code (for display/reference)
   */
  function getCurrentCountry() {
    return window.VPMSizeConfig?.shopifyCountry || sessionStorage.getItem(CONFIG.countryStorageKey) || null;
  }

  function getSystemForCountry(countryCode) {
    if (!countryCode) return CONFIG.defaultSystem;
    return COUNTRY_TO_SYSTEM[countryCode.toUpperCase()] || CONFIG.defaultSystem;
  }

  // ============================================
  // USER PREFERENCE
  // ============================================

  const LAST_COUNTRY_KEY = 'vpm_last_country';

  function getUserPreference() {
    try {
      return localStorage.getItem(CONFIG.storageKey);
    } catch (e) {
      return null;
    }
  }

  function setUserPreference(system) {
    try {
      localStorage.setItem(CONFIG.storageKey, system);
      log('Saved preference:', system);
    } catch (e) {
      log('Could not save preference');
    }
  }

  function clearUserPreference() {
    try {
      localStorage.removeItem(CONFIG.storageKey);
      log('Cleared user preference');
    } catch (e) {
      // Ignore
    }
  }

  /**
   * Check if country changed since last visit and clear preference if so.
   * This ensures footer country selector changes take effect.
   */
  function checkCountryChange() {
    const currentCountry = window.VPMSizeConfig?.shopifyCountry;
    if (!currentCountry) return;

    try {
      const lastCountry = localStorage.getItem(LAST_COUNTRY_KEY);

      if (lastCountry && lastCountry !== currentCountry) {
        // Country changed via footer selector - keep size preference, only clear session cache
        log('Country changed from', lastCountry, 'to', currentCountry);
        sessionStorage.removeItem(CONFIG.countryStorageKey);
      }

      // Save current country for next comparison
      localStorage.setItem(LAST_COUNTRY_KEY, currentCountry);
    } catch (e) {
      log('Error checking country change:', e);
    }
  }

  // ============================================
  // SIZE PICKER COMPONENT
  // ============================================

  class VPMSizePicker {
    constructor(container, variants, optionValues, options = {}) {
      this.container = container;
      this.variants = variants;
      this.optionValues = optionValues || []; // Option values with IDs from Liquid
      this.options = options;
      this.currentSystem = CONFIG.defaultSystem;
      this.selectedVariant = null;
      this.isExpanded = false;
      this.sizes = [];

      // Bind methods that need 'this' context
      this.handleModalOutsideClick = this.handleModalOutsideClick.bind(this);

      // Get config from container data attributes
      this.optionName = container.dataset.optionName || 'Size';
      this.optionPosition = container.dataset.optionPosition || '1';
      this.productFormId = container.dataset.productFormId || '';

      // Extract section ID from product form ID (format: "product-form-{section_id}")
      this.sectionId = this.productFormId.replace('product-form-', '');
      // Option index is 0-based (position - 1)
      this.optionIndex = parseInt(this.optionPosition, 10) - 1;

      this.init();
    }

    async init() {
      log('Initializing size picker');
      log('Option name:', this.optionName);
      log('Option values:', this.optionValues);

      // Parse variants to extract sizes
      this.parseVariants();

      // Determine initial size system
      const savedPreference = getUserPreference();
      if (savedPreference) {
        this.currentSystem = savedPreference;
      } else {
        const country = await detectCountry();
        this.currentSystem = getSystemForCountry(country);
      }

      log('Using size system:', this.currentSystem);

      // Render the component
      this.render();
      this.bindEvents();

      // Select first available size if none selected
      // Pass false for dispatchEvent to prevent theme from replacing our picker
      if (!this.selectedVariant) {
        const firstAvailable = this.sizes.find(s => s.available);
        if (firstAvailable) {
          this.selectSize(firstAvailable, false, false); // Don't collapse, don't dispatch event
        }
      }
    }

    parseVariants() {
      this.sizes = [];

      // Create a map of option values for quick lookup by value string
      const optionValueMap = new Map();
      this.optionValues.forEach(ov => {
        optionValueMap.set(ov.value, ov);
      });

      this.variants.forEach(variant => {
        // Get size from option1, option2, or option3
        let sizeValue = null;
        for (let i = 1; i <= 3; i++) {
          const opt = variant[`option${i}`];
          if (opt && (
            opt.match(/US\s*[\d.]+/i) ||    // Matches "US7", "US 7"
            opt.match(/EU\s*[\d.]+/i) ||    // Matches "EU40", "EU 40"
            opt.match(/^[\d.]+$/)           // Matches plain numbers like "36", "40.5" (localized EU)
          )) {
            sizeValue = opt;
            break;
          }
        }

        if (sizeValue) {
          const parsed = parseSizeString(sizeValue);
          if (parsed) {
            // Get option value ID from the Liquid-provided data
            const optionValueData = optionValueMap.get(sizeValue);

            this.sizes.push({
              variant: variant,
              variantId: variant.id,
              optionValueId: optionValueData?.id || null, // Shopify option value ID
              productUrl: optionValueData?.productUrl || null,
              original: sizeValue,
              parsed: parsed,
              available: variant.available
            });
          }
        }
      });

      // Sort by EU size
      this.sizes.sort((a, b) => {
        const aEU = parseFloat(a.parsed.EU) || 0;
        const bEU = parseFloat(b.parsed.EU) || 0;
        return aEU - bEU;
      });

      log('Parsed sizes:', this.sizes);
    }

    render() {
      // Generate select ID matching theme's format: Option-{section_id}-{option_index}
      const selectId = `Option-${this.sectionId}-${this.optionIndex}`;
      log('Generated select ID:', selectId);
      log('Section ID:', this.sectionId, 'Option position:', this.optionPosition, 'Option index:', this.optionIndex);

      const html = `
        <div class="vpm-size-picker" data-expanded="false">
          <!-- Hidden select for Shopify theme compatibility -->
          <!-- This syncs with the custom UI and allows variant-selects to work correctly -->
          <select
            id="${selectId}"
            class="vpm-size-picker__hidden-select visually-hidden"
            name="options[${this.optionName}]"
            ${this.productFormId ? `form="${this.productFormId}"` : ''}
            aria-hidden="true"
            tabindex="-1">
            ${this.sizes.map((size, index) => `
              <option
                id="${this.sectionId}-${this.optionPosition}-${index}"
                value="${size.original}"
                data-option-value-id="${size.optionValueId || ''}"
                data-product-url="${size.productUrl || ''}"
                ${!size.available ? 'disabled' : ''}>
                ${size.original}
              </option>
            `).join('')}
          </select>

          <div class="vpm-size-picker__toggle">
            <span class="vpm-size-picker__label">SIZE (<span class="vpm-size-picker__system-label">${SYSTEM_LABELS[this.currentSystem]}</span>):</span>
            <span class="vpm-size-picker__selected-value"></span>
            <span class="vpm-size-picker__chevron">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </div>

          <div class="vpm-size-picker__panel">
            <div class="vpm-size-picker__tabs">
              ${SYSTEM_ORDER.map(system => `
                <button
                  type="button"
                  class="vpm-size-picker__tab ${system === this.currentSystem ? 'is-active' : ''}"
                  data-system="${system}">
                  ${SYSTEM_LABELS[system]}
                </button>
              `).join('')}
              <div class="vpm-size-picker__tab-indicator"></div>
            </div>

            <div class="vpm-size-picker__sizes">
              ${this.renderSizeList()}
            </div>

            <div class="vpm-size-picker__footer">
              <span class="vpm-size-picker__footer-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="8" fill="none" viewBox="0 0 13 8">
                  <path fill="#24201C" d="M1 7h2V6h1v1h2V6h1v1h2V6h1v1h2V4h1v4H0V4h1v3Zm10.5-5.503L9.5 3V2h-6v1l-2-1.503L3.5 0v1h6V0l2 1.497Z"/>
                </svg>
              </span>
              <button type="button" class="vpm-size-picker__size-guide-btn">SIZE GUIDE</button>
            </div>
          </div>

          <input type="hidden" name="id" value="" class="vpm-size-picker__variant-input">
        </div>
      `;

      this.container.innerHTML = html;

      // Create modal separately and append to body
      this.createModal();

      // Cache DOM references
      this.picker = this.container.querySelector('.vpm-size-picker');
      this.toggle = this.container.querySelector('.vpm-size-picker__toggle');
      this.panel = this.container.querySelector('.vpm-size-picker__panel');
      this.tabs = this.container.querySelectorAll('.vpm-size-picker__tab');
      this.indicator = this.container.querySelector('.vpm-size-picker__tab-indicator');
      this.sizeList = this.container.querySelector('.vpm-size-picker__sizes');
      this.variantInput = this.container.querySelector('.vpm-size-picker__variant-input');
      this.selectedValue = this.container.querySelector('.vpm-size-picker__selected-value');
      this.systemLabel = this.container.querySelector('.vpm-size-picker__system-label');
      this.footer = this.container.querySelector('.vpm-size-picker__footer');
      this.hiddenSelect = this.container.querySelector('.vpm-size-picker__hidden-select');

      // Modal references (modal is appended to body)
      this.sizeGuideBtn = this.container.querySelector('.vpm-size-picker__size-guide-btn');

      // Position the indicator on the active tab
      this.updateIndicator();
    }

    createModal() {
      // Check if modal already exists
      const existingModal = document.getElementById('vpm-size-modal');
      if (existingModal) {
        this.modal = existingModal;
        this.modalBackdrop = this.modal.querySelector('.vpm-size-modal__backdrop');
        this.modalClose = this.modal.querySelector('.vpm-size-modal__close');
        return;
      }

      // Build size data for chart (matching screenshot layout)
      const euSizes = Object.keys(SIZE_CONVERSIONS);
      const sizeData = euSizes.map(eu => {
        const conv = SIZE_CONVERSIONS[eu];
        return {
          eu,
          usM: conv.US,
          usW: (parseFloat(conv.US) + 1.5).toString(), // US Women = US Men + 1.5
          uk: conv.UK,
          cm: conv.JPN
        };
      });

      const modalHtml = `
        <div class="vpm-size-modal" id="vpm-size-modal" hidden>
          <div class="vpm-size-modal__backdrop"></div>
          <div class="vpm-size-modal__content">
            <div class="vpm-size-modal__header">
              <h3 class="vpm-size-modal__title">SIZING CHART</h3>
              <button type="button" class="vpm-size-modal__close" aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
            <div class="vpm-size-modal__body">
              <div class="vpm-size-chart">
                <div class="vpm-size-chart__row">
                  <div class="vpm-size-chart__label">US M</div>
                  ${sizeData.map(s => `<div class="vpm-size-chart__cell">${s.usM}</div>`).join('')}
                </div>
                <div class="vpm-size-chart__row">
                  <div class="vpm-size-chart__label">US W</div>
                  ${sizeData.map(s => `<div class="vpm-size-chart__cell">${s.usW}</div>`).join('')}
                </div>
                <div class="vpm-size-chart__row">
                  <div class="vpm-size-chart__label">UK</div>
                  ${sizeData.map(s => `<div class="vpm-size-chart__cell">${s.uk}</div>`).join('')}
                </div>
                <div class="vpm-size-chart__row">
                  <div class="vpm-size-chart__label">EU</div>
                  ${sizeData.map(s => `<div class="vpm-size-chart__cell">${s.eu}</div>`).join('')}
                </div>
                <div class="vpm-size-chart__row">
                  <div class="vpm-size-chart__label">CM</div>
                  ${sizeData.map(s => `<div class="vpm-size-chart__cell">${s.cm}</div>`).join('')}
                </div>
              </div>
              <p class="vpm-size-modal__note">Footwear is unisex. In case of doubt please go half a size up.</p>
            </div>
          </div>
        </div>
      `;

      // Insert modal as sibling of .vpm-product-single-size-picker (one level above our container)
      const sizePickerWrapper = this.container.closest('.vpm-product-single-size-picker');
      if (sizePickerWrapper && sizePickerWrapper.parentElement) {
        sizePickerWrapper.parentElement.insertAdjacentHTML('beforeend', modalHtml);
      } else {
        // Fallback: insert after our container's parent
        this.container.parentElement.insertAdjacentHTML('beforeend', modalHtml);
      }

      // Cache references
      this.modal = document.getElementById('vpm-size-modal');
      this.modalBackdrop = this.modal.querySelector('.vpm-size-modal__backdrop');
      this.modalClose = this.modal.querySelector('.vpm-size-modal__close');
    }

    renderSizeList() {
      return this.sizes.map(size => {
        const displayValue = formatSizeDisplay(size.parsed[this.currentSystem], this.currentSystem);
        const isSelected = this.selectedVariant && this.selectedVariant.variantId === size.variantId;
//${!size.available ? 'disabled' : ''} removed from <button> below
        return `
          <button
            type="button"
            class="vpm-size-picker__size ${isSelected ? 'is-selected' : ''} ${!size.available ? 'is-unavailable' : ''}"
            data-variant-id="${size.variantId}"
            >
            <span class="vpm-size-picker__size-value">${displayValue}</span>
            ${!size.available ? '<span class="vpm-size-picker__size-status">SOLD OUT</span>' : ''}
          </button>
        `;
      }).join('');
    }

    bindEvents() {
      // Toggle expand/collapse
      this.toggle.addEventListener('click', () => this.toggleExpand());

      // Tab switching
      this.tabs.forEach(tab => {
        tab.addEventListener('click', (e) => {
          const system = e.currentTarget.dataset.system;
          this.switchSystem(system);
        });
      });

      // Size Guide modal
      if (this.sizeGuideBtn) {
        this.sizeGuideBtn.addEventListener('click', (e) => {
          e.stopPropagation(); // Prevent panel collapse
          this.openModal();
        });
      }

      if (this.modalClose) {
        this.modalClose.addEventListener('click', () => this.closeModal());
      }

      if (this.modalBackdrop) {
        this.modalBackdrop.addEventListener('click', () => this.closeModal());
      }

      // Size selection
      this.sizeList.addEventListener('click', (e) => {
        const sizeBtn = e.target.closest('.vpm-size-picker__size');
        if (sizeBtn && !sizeBtn.disabled) {
          const variantId = sizeBtn.dataset.variantId;
          const size = this.sizes.find(s => s.variantId == variantId);
          if (size) {
            this.selectSize(size, true); // Collapse after selection
          }
        }
      });

      // Close on outside click
      document.addEventListener('click', (e) => {
        if (this.isExpanded && !this.container.contains(e.target)) {
          this.collapse();
        }
      });

      // Handle keyboard
      this.container.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isExpanded) {
          this.collapse();
        }
      });
    }

    toggleExpand() {
      if (this.isExpanded) {
        this.collapse();
      } else {
        this.expand();
      }
    }

    expand() {
      this.isExpanded = true;
      this.picker.dataset.expanded = 'true';
      this.picker.classList.add('is-expanded');

      // Update indicator position after panel is visible
      requestAnimationFrame(() => {
        this.updateIndicator();
      });

      log('Expanded picker');
    }

    collapse() {
      this.isExpanded = false;
      this.picker.dataset.expanded = 'false';
      this.picker.classList.remove('is-expanded');
      log('Collapsed picker');
    }

    openModal() {
      if (this.modal) {
        // Close the size picker panel first
        this.collapse();

        this.modal.hidden = false;

        // Add click outside listener (delayed to prevent immediate close)
        setTimeout(() => {
          document.addEventListener('click', this.handleModalOutsideClick);
        }, 10);

        log('Opened size guide modal');
      }
    }

    closeModal() {
      if (this.modal) {
        this.modal.hidden = true;

        // Remove click outside listener
        document.removeEventListener('click', this.handleModalOutsideClick);

        log('Closed size guide modal');
      }
    }

    handleModalOutsideClick(e) {
      // Close modal if clicking outside the modal content
      const modalContent = this.modal.querySelector('.vpm-size-modal__content');
      if (modalContent && !modalContent.contains(e.target)) {
        this.closeModal();
      }
    }

    switchSystem(system) {
      if (system === this.currentSystem) return;

      this.currentSystem = system;
      setUserPreference(system);

      // Update tabs
      this.tabs.forEach(tab => {
        tab.classList.toggle('is-active', tab.dataset.system === system);
      });

      // Animate indicator
      this.updateIndicator();

      // Re-render sizes
      this.sizeList.innerHTML = this.renderSizeList();

      // Update selected label if a size is selected
      if (this.selectedVariant) {
        this.updateSelectedLabel();
      }

      // Dispatch event for cart drawer update
      document.dispatchEvent(new CustomEvent('vpm:size-system-changed', {
        detail: { system }
      }));

      log('Switched to system:', system);
    }

    updateIndicator() {
      const activeTab = this.container.querySelector('.vpm-size-picker__tab.is-active');
      if (activeTab && this.indicator) {
        const tabRect = activeTab.getBoundingClientRect();
        const containerRect = activeTab.parentElement.getBoundingClientRect();

        this.indicator.style.width = `${tabRect.width}px`;
        this.indicator.style.left = `${tabRect.left - containerRect.left}px`;
      }
    }

    selectSize(size, shouldCollapse = true, updateVariant = true) {
      this.selectedVariant = size;

      // Update UI - mark selected size
      this.sizeList.querySelectorAll('.vpm-size-picker__size').forEach(btn => {
        btn.classList.toggle('is-selected', btn.dataset.variantId == size.variantId);
      });

      // Update our hidden variant input
      this.variantInput.value = size.variantId;

      // Update selected label in toggle bar
      this.updateSelectedLabel();

      // Update the hidden select (for form submission)
      if (this.hiddenSelect) {
        Array.from(this.hiddenSelect.options).forEach(opt => {
          const isMatch = opt.value === size.original;
          opt.selected = isMatch;
          if (isMatch) {
            opt.setAttribute('selected', 'selected');
          } else {
            opt.removeAttribute('selected');
          }
        });
      }

      // Update variant in the page (URL, price, add-to-cart) without triggering theme re-render
      if (updateVariant) {
        this.updateVariantInPage(size);
      }

      // Collapse if requested
      if (shouldCollapse) {
        setTimeout(() => this.collapse(), 150);
      }

      log('Selected size:', size);
    }

    updateVariantInPage(size) {
      const variant = size.variant;

      // 1. Update URL with variant ID
      const url = new URL(window.location.href);
      url.searchParams.set('variant', size.variantId);
      window.history.replaceState({}, '', url.toString());
      log('Updated URL with variant:', size.variantId);

      // 2. Update the product form's hidden variant ID input
      const productForm = document.querySelector(`#${this.productFormId}, form[action*="/cart/add"]`);
      if (productForm) {
        let variantInput = productForm.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = size.variantId;
          log('Updated form variant input:', size.variantId);
        }
      }

      // 3. Update price display
      this.updatePriceDisplay(variant);

      // 4. Update add-to-cart button state (enabled/disabled based on availability)
      this.updateAddToCartButton(variant);

      // 5. Dispatch custom event for any other listeners (but not the theme's re-render)
      document.dispatchEvent(new CustomEvent('vpm:variant-changed', {
        detail: { variant, size }
      }));
    }

    updatePriceDisplay(variant) {
      if (!variant) return;

      // Find price elements
      const priceContainer = document.querySelector('.price');
      if (!priceContainer) return;

      const regularPrice = priceContainer.querySelector('.price-item--regular');
      const comparePrice = priceContainer.querySelector('.price-item--sale');

      // Format price (Shopify stores in cents)
      const formatPrice = (cents) => {
        const currency = window.Shopify?.currency?.active || 'EUR';
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currency
        }).format(cents / 100);
      };

      if (regularPrice && variant.price) {
        regularPrice.textContent = formatPrice(variant.price);
      }

      if (comparePrice && variant.compare_at_price) {
        comparePrice.textContent = formatPrice(variant.compare_at_price);
        priceContainer.classList.add('price--on-sale');
      } else if (priceContainer) {
        priceContainer.classList.remove('price--on-sale');
      }

      log('Updated price display:', variant.price);
    }

    updateAddToCartButton(variant) {
      const addButton = document.querySelector('[name="add"], .product-form__submit');
      if (!addButton) return;

      if (variant && variant.available) {
        addButton.disabled = false;
        const buttonText = addButton.querySelector('.btn-text, span') || addButton;
        if (buttonText.textContent.includes('Sold out') || buttonText.textContent.includes('Unavailable')) {
          buttonText.textContent = 'Add to cart';
        }
      } else {
        addButton.disabled = true;
        const buttonText = addButton.querySelector('.btn-text, span') || addButton;
        buttonText.textContent = 'Sold out';
      }

      log('Updated add-to-cart button, available:', variant?.available);
    }

    updateSelectedLabel() {
      // Update the selected value in the toggle bar
      if (this.selectedVariant && this.selectedValue) {
        const value = this.selectedVariant.parsed[this.currentSystem];
        this.selectedValue.textContent = value; // Just the number (e.g., "9.5")
      }

      // Update the system label (e.g., "US" in "SIZE (US):")
      if (this.systemLabel) {
        this.systemLabel.textContent = SYSTEM_LABELS[this.currentSystem];
      }
    }

    triggerVariantChange(size) {
      // Update the URL with variant ID
      const url = new URL(window.location.href);
      url.searchParams.set('variant', size.variantId);
      window.history.replaceState({}, '', url.toString());

      // Dispatch custom event that Shopify themes listen for
      const event = new CustomEvent('variant:change', {
        detail: {
          variant: size.variant
        },
        bubbles: true
      });
      this.container.dispatchEvent(event);

      // Also trigger on the variant-selects element if it exists
      const variantSelects = document.querySelector('variant-selects');
      if (variantSelects && typeof variantSelects.onVariantChange === 'function') {
        // Some themes have this method
      }

      // Update price display
      this.updatePrice(size.variant);
    }

    updatePrice(variant) {
      // Update price elements on the page
      const priceContainer = document.querySelector('.price');
      if (priceContainer && variant) {
        const priceElement = priceContainer.querySelector('.price-item--regular');
        if (priceElement && variant.price) {
          // Format price (Shopify stores in cents)
          const formattedPrice = (variant.price / 100).toLocaleString('en-US', {
            style: 'currency',
            currency: window.Shopify?.currency?.active || 'EUR'
          });
          priceElement.textContent = formattedPrice;
        }
      }
    }

    // Public method to get current state
    getState() {
      return {
        system: this.currentSystem,
        selectedVariant: this.selectedVariant,
        isExpanded: this.isExpanded
      };
    }
  }

  // ============================================
  // CART DRAWER INTEGRATION
  // ============================================

  function transformCartSizes(system) {
    // Find size options in cart drawer and cart page
    const cartOptions = document.querySelectorAll('.product-option[data-option-name="size"] dd[data-option-value]');

    log('Found cart size options:', cartOptions.length);

    cartOptions.forEach(element => {
      // Store original value if not already stored
      if (!element.dataset.originalValue) {
        element.dataset.originalValue = element.textContent.trim();
      }

      const originalText = element.dataset.originalValue;
      const parsed = parseSizeString(originalText);

      if (parsed && parsed[system]) {
        const newValue = formatSizeLabel(parsed[system], system);
        // Only update if different (avoid unnecessary DOM updates)
        if (element.textContent.trim() !== newValue) {
          element.textContent = newValue;
          log('Transformed cart size:', originalText, '->', newValue);
        }
      }
    });

    log('Transformed cart sizes to:', system);
  }

  function transformProductCardSizes(system) {
    // Find size elements in product cards (collection pages, search results, etc.)
    const sizeElements = document.querySelectorAll('[data-vpm-size]');

    log('Found product card size elements:', sizeElements.length);

    sizeElements.forEach(element => {
      const originalText = element.dataset.originalValue || element.textContent.trim();

      // Ensure original value is stored
      if (!element.dataset.originalValue) {
        element.dataset.originalValue = originalText;
      }

      const parsed = parseSizeString(originalText);

      if (parsed && parsed[system]) {
        const newValue = formatSizeDisplay(parsed[system], system);
        // Only update if different
        if (element.textContent.trim() !== newValue) {
          element.textContent = newValue;
        }
      }
    });

    log('Transformed product card sizes to:', system);
  }

  function transformAllSizes(system) {
    transformCartSizes(system);
    transformProductCardSizes(system);
  }

  function setupCartObserver() {
    // Watch the cart drawer for content changes
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      const observer = new MutationObserver((mutations) => {
        // Check if cart items were added/updated
        const hasCartChanges = mutations.some(mutation => {
          return mutation.addedNodes.length > 0 ||
            (mutation.type === 'childList' && mutation.target.closest('.cart-items'));
        });

        if (hasCartChanges) {
          const system = getUserPreference() || CONFIG.defaultSystem;
          // Small delay to ensure DOM is fully updated
          requestAnimationFrame(() => {
            transformCartSizes(system);
          });
        }
      });

      observer.observe(cartDrawer, {
        childList: true,
        subtree: true
      });

      log('Cart drawer observer set up');

      // Also watch for drawer visibility changes (when it opens)
      const drawerInner = cartDrawer.querySelector('.drawer__inner');
      if (drawerInner) {
        const visibilityObserver = new MutationObserver(() => {
          // Check if drawer is now visible
          const drawer = cartDrawer.querySelector('.drawer');
          if (drawer && !drawer.classList.contains('is-empty')) {
            const system = getUserPreference() || CONFIG.defaultSystem;
            transformCartSizes(system);
          }
        });

        visibilityObserver.observe(cartDrawer, {
          attributes: true,
          attributeFilter: ['class', 'open'],
          subtree: true
        });
      }
    }

    // Watch the main cart page for content changes
    const cartItems = document.querySelector('cart-items');
    if (cartItems) {
      const cartObserver = new MutationObserver((mutations) => {
        const hasCartChanges = mutations.some(mutation => {
          return mutation.addedNodes.length > 0 ||
            (mutation.type === 'childList' && mutation.target.closest('.cart-items, .vpm-cart-items'));
        });

        if (hasCartChanges) {
          const system = getUserPreference() || CONFIG.defaultSystem;
          requestAnimationFrame(() => {
            transformCartSizes(system);
          });
        }
      });

      cartObserver.observe(cartItems, {
        childList: true,
        subtree: true
      });

      log('Main cart observer set up');
    }
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  function initSizePickers() {
    if (!CONFIG.enabled) {
      log('Size localization disabled');
      return;
    }

    log('Looking for size picker containers...');

    const containers = document.querySelectorAll('[data-vpm-size-picker]');

    containers.forEach(container => {
      // Skip if already initialized
      if (container.dataset.vpmInitialized) {
        log('Container already initialized, skipping');
        return;
      }

      // Get variants data
      const variantsScript = container.nextElementSibling?.matches('[data-size-variants]')
        ? container.nextElementSibling
        : document.querySelector('[data-size-variants]');

      if (!variantsScript) {
        log('No variants data found');
        return;
      }

      // Get option values data (with IDs for theme compatibility)
      const optionValuesScript = variantsScript.nextElementSibling?.matches('[data-size-option-values]')
        ? variantsScript.nextElementSibling
        : document.querySelector('[data-size-option-values]');

      try {
        const variants = JSON.parse(variantsScript.textContent);
        const optionValues = optionValuesScript
          ? JSON.parse(optionValuesScript.textContent)
          : [];

        log('Variants:', variants.length, 'Option values:', optionValues.length);

        new VPMSizePicker(container, variants, optionValues);

        // Mark as initialized to prevent double init
        container.dataset.vpmInitialized = 'true';
      } catch (e) {
        log('Error parsing data:', e);
      }
    });
  }

  // Listen for system changes to update all sizes (cart, product cards, etc.)
  document.addEventListener('vpm:size-system-changed', (e) => {
    transformAllSizes(e.detail.system);
  });

  // Re-init on Shopify section load (for theme editor)
  document.addEventListener('shopify:section:load', initSizePickers);

  // Subscribe to Shopify theme's cart update events (uses pub/sub system)
  function subscribeToCartUpdates() {
    // Check if the theme's pub/sub system is available
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        log('Cart update event received:', event?.source);
        const system = getUserPreference() || CONFIG.defaultSystem;
        // Delay slightly to ensure DOM is updated
        setTimeout(() => transformAllSizes(system), 150);
      });
      log('Subscribed to cart update events via pub/sub');
    } else {
      log('Pub/sub system not available, using fallback observers');
    }
  }

  // Watch for variant-selects content changes (theme updates HTML after variant change)
  // This ensures our picker reinitializes after the theme replaces the HTML
  function setupMutationObserver() {
    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) return;

    const observer = new MutationObserver((mutations) => {
      // Check if our picker was removed/replaced
      const hasPickerContainer = variantSelects.querySelector('[data-vpm-size-picker]');
      const isInitialized = hasPickerContainer?.dataset.vpmInitialized === 'true';

      if (hasPickerContainer && !isInitialized) {
        log('Picker container found but not initialized, reinitializing...');
        // Small delay to ensure DOM is fully updated
        requestAnimationFrame(() => {
          initSizePickers();
        });
      }
    });

    observer.observe(variantSelects, {
      childList: true,
      subtree: true
    });

    log('MutationObserver set up for variant-selects');
  }

  /**
   * Get the appropriate size system based on user preference or country detection
   */
  async function getInitialSystem() {
    // Priority 1: User's explicit tab selection
    const savedPreference = getUserPreference();
    if (savedPreference) {
      log('Using saved user preference:', savedPreference);
      return savedPreference;
    }

    // Priority 2: Detect country and map to size system
    const country = await detectCountry();
    const system = getSystemForCountry(country);
    log('Using country-based system:', system, '(country:', country + ')');
    return system;
  }

  /**
   * Initialize all size localization features
   */
  async function initAll() {
    // Check if country changed (via footer selector) and clear preference if needed
    checkCountryChange();

    initSizePickers();
    setupMutationObserver();
    setupCartObserver();
    subscribeToCartUpdates();

    // Transform all sizes on page load (cart, product cards, etc.)
    const system = await getInitialSystem();
    transformAllSizes(system);
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAll();
    });
  } else {
    initAll();
  }

  // ============================================
  // PUBLIC API
  // ============================================

  window.VPMSizeLocalization = {
    init: initSizePickers,
    initAll: initAll,
    parseSize: parseSizeString,
    formatSize: formatSizeLabel,
    getConversions: () => SIZE_CONVERSIONS,
    getSystems: () => SYSTEM_ORDER,
    getCountryToSystem: () => COUNTRY_TO_SYSTEM,
    getCurrentCountry: getCurrentCountry,
    getInitialSystem: getInitialSystem,
    getUserPreference: getUserPreference,
    clearPreference: clearUserPreference,
    transformCartSizes: transformCartSizes,
    transformProductCardSizes: transformProductCardSizes,
    transformAllSizes: transformAllSizes,
    setDebug: (enabled) => { CONFIG.debug = enabled; }
  };

})();
